import type { Airport } from './api/flights.types';
import type { FlightSearchValues } from './flightSearchValues';
import { totalPassengers } from './flightSearchValues';

export type FlightSearchField = 'origin' | 'destination' | 'dates' | 'passengers';

export type FlightSearchErrors = Partial<Record<FlightSearchField, string>>;

export const SAME_ORIGIN_DESTINATION_MESSAGE =
  'No podés realizar una búsqueda con el mismo origen y destino.';

const MESSAGES: Record<FlightSearchField, string> = {
  dates: 'Elegí las fechas del viaje',
  destination: 'Ingresá tu destino',
  origin: 'Ingresá tu origen',
  passengers: 'Elegí la cantidad de pasajeros',
};

// Ordered the way the fields read in the card, so the summary lists them in
// the same order the user sees them.
const FIELD_LABELS: [FlightSearchField, string][] = [
  ['origin', 'origen'],
  ['destination', 'destino'],
  ['dates', 'fechas'],
  ['passengers', 'pasajeros'],
];

function isUsable(airport: Airport | null): boolean {
  return Boolean(airport?.cityCode && String(airport.cityName || '').trim());
}

/** Same endpoint comparison the legacy validator uses: city code, case-insensitive. */
export function endpointsAreSame(origin: Airport | null, destination: Airport | null): boolean {
  const a = (origin?.airportCode || origin?.cityCode || '').trim().toUpperCase();
  const b = (destination?.airportCode || destination?.cityCode || '').trim().toUpperCase();
  const cityA = (origin?.cityCode || '').trim().toUpperCase();
  const cityB = (destination?.cityCode || '').trim().toUpperCase();

  return Boolean((a && b && a === b) || (cityA && cityB && cityA === cityB));
}

/**
 * Typed port of `validateFlightsSearchSubmit`, returned per field so the search
 * card can mark every invalid input at once.
 */
export function validateFlightSearch(values: FlightSearchValues): FlightSearchErrors {
  const errors: FlightSearchErrors = {};

  if (!isUsable(values.origin)) errors.origin = MESSAGES.origin;
  if (!isUsable(values.destination)) errors.destination = MESSAGES.destination;

  if (
    !errors.origin &&
    !errors.destination &&
    endpointsAreSame(values.origin, values.destination)
  ) {
    errors.destination = SAME_ORIGIN_DESTINATION_MESSAGE;
  }

  const hasDeparture = Boolean(values.departureDate);
  const hasReturn = Boolean(values.returnDate);
  if (!hasDeparture || (!values.isOnlyOneWay && !hasReturn)) {
    errors.dates = MESSAGES.dates;
  }

  if (totalPassengers(values.passengers) < 1) {
    errors.passengers = MESSAGES.passengers;
  }

  return errors;
}

/** Single sentence summarizing the validation result, for the form-level message. */
export function summarizeFlightErrors(errors: FlightSearchErrors): string {
  if (errors.destination === SAME_ORIGIN_DESTINATION_MESSAGE) {
    return SAME_ORIGIN_DESTINATION_MESSAGE;
  }

  const labels = FIELD_LABELS.filter(([field]) => Boolean(errors[field])).map(([, label]) => label);

  if (labels.length === 0) return '';
  if (labels.length === 1) return `Completá ${labels[0]} para continuar.`;

  const last = labels[labels.length - 1];
  return `Completá ${labels.slice(0, -1).join(', ')} y ${last} para continuar.`;
}
